import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useLocale } from '../i18n/LocaleContext';

// One config entry per account-level provider - status/connect/disconnect API calls are
// consistent enough (see api.js) that a single generic card renderer below can drive all
// five, instead of near-duplicate JSX per provider (which is exactly how the header's
// "Connect GitHub" button and this whole screen's absence happened: connecting to a
// provider was scattered across the Builder wizard and the Publishing screen, so a
// non-technical customer had no single place to see what's connected and what isn't).
const SOURCE_PROVIDERS = [
  { id: 'github', descriptionKey: 'connections.githubDescription' }
];

const DEPLOY_PROVIDERS = [
  { id: 'cloudflare-pages', apiPrefix: 'cloudflare', descriptionKey: 'connections.cloudflareDescription' },
  { id: 'netlify', apiPrefix: 'netlify', descriptionKey: 'connections.netlifyDescription' },
  { id: 'vercel', apiPrefix: 'vercel', descriptionKey: 'connections.vercelDescription' },
  { id: 'firebase-hosting', apiPrefix: 'firebase', descriptionKey: 'connections.firebaseDescription' }
];

function apiMethod(prefix, action) {
  // github's methods are named githubStatus/githubConnect/githubDisconnect; the deploy
  // providers follow the exact same <prefix>Status/<prefix>Connect/<prefix>Disconnect
  // pattern (see api.js) - this just resolves the right function by name.
  return api[`${prefix}${action}`];
}

function ProviderCard({ name, description, connected, accountLabel, loading, onConnect, onDisconnect, busy }) {
  const { t } = useLocale();
  return (
    <div className="connection-card">
      <div className="connection-card-main">
        <div className="connection-card-name">{name}</div>
        <p className="connection-card-description">{description}</p>
        {connected && accountLabel && <div className="connection-card-account">{accountLabel}</div>}
      </div>
      <div className="connection-card-side">
        <span className={`connection-status-badge ${connected ? 'connected' : 'disconnected'}`}>
          <span className="connection-status-dot" />
          {loading ? t('common.loading') : connected ? t('connections.connected') : t('connections.notConnected')}
        </span>
        {!loading && (
          connected
            ? <button type="button" className="btn connection-disconnect-btn" onClick={onDisconnect} disabled={busy}>{t('connections.disconnect')}</button>
            : <button type="button" className="submit-btn connection-connect-btn" onClick={onConnect} disabled={busy}>{t('connections.connect')}</button>
        )}
      </div>
    </div>
  );
}

function ConnectionsPanel({ onNavigateToPublishing }) {
  const { t } = useLocale();
  const [github, setGithub] = useState({ connected: false, username: null, loading: true });
  const [deployState, setDeployState] = useState({
    'cloudflare-pages': { connected: false, accountName: null, loading: true },
    'netlify': { connected: false, loading: true },
    'vercel': { connected: false, loading: true },
    'firebase-hosting': { connected: false, loading: true }
  });
  const [google, setGoogle] = useState({ connected: false, configured: true, loading: true });
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const loadAll = () => {
    api.githubStatus().then(res => setGithub({ connected: res.connected, username: res.username, loading: false })).catch(() => setGithub(g => ({ ...g, loading: false })));
    api.cloudflareStatus().then(res => setDeployState(s => ({ ...s, 'cloudflare-pages': { connected: res.connected, accountName: res.accountName, loading: false } }))).catch(() => setDeployState(s => ({ ...s, 'cloudflare-pages': { ...s['cloudflare-pages'], loading: false } })));
    api.netlifyStatus().then(res => setDeployState(s => ({ ...s, netlify: { connected: res.connected, loading: false } }))).catch(() => setDeployState(s => ({ ...s, netlify: { ...s.netlify, loading: false } })));
    api.vercelStatus().then(res => setDeployState(s => ({ ...s, vercel: { connected: res.connected, loading: false } }))).catch(() => setDeployState(s => ({ ...s, vercel: { ...s.vercel, loading: false } })));
    api.firebaseStatus().then(res => setDeployState(s => ({ ...s, 'firebase-hosting': { connected: res.connected, loading: false } }))).catch(() => setDeployState(s => ({ ...s, 'firebase-hosting': { ...s['firebase-hosting'], loading: false } })));
    api.publishingStatus().then(res => setGoogle({ connected: res.connected, configured: res.configured, loading: false })).catch(() => setGoogle(g => ({ ...g, loading: false })));
  };

  useEffect(() => { loadAll(); }, []);

  const handleConnect = async (id, apiPrefix) => {
    setError('');
    setBusyId(id);
    try {
      const res = await apiMethod(apiPrefix, 'Connect')();
      window.location.href = res.authorizeUrl;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setBusyId(null);
    }
  };

  const handleDisconnect = async (id, apiPrefix) => {
    setError('');
    setBusyId(id);
    try {
      await apiMethod(apiPrefix, 'Disconnect')();
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const PROVIDER_NAMES = {
    'github': 'GitHub',
    'cloudflare-pages': 'Cloudflare Pages',
    'netlify': 'Netlify',
    'vercel': 'Vercel',
    'firebase-hosting': 'Firebase Hosting'
  };

  return (
    <div className="connections-panel">
      <div className="connections-header">
        <div className="connections-title">{t('connections.title')}</div>
        <p className="connections-subtitle">{t('connections.subtitle')}</p>
      </div>

      {error && <div className="connections-error">{error}</div>}

      <div className="connections-section">
        <div className="connections-section-title">{t('connections.sourceCodeSection')}</div>
        <div className="connections-list">
          {SOURCE_PROVIDERS.map(p => (
            <ProviderCard
              key={p.id}
              name={PROVIDER_NAMES[p.id]}
              description={t(p.descriptionKey)}
              connected={github.connected}
              accountLabel={github.username ? `@${github.username}` : null}
              loading={github.loading}
              busy={busyId === p.id}
              onConnect={() => handleConnect(p.id, 'github')}
              onDisconnect={() => handleDisconnect(p.id, 'github')}
            />
          ))}
        </div>
      </div>

      <div className="connections-section">
        <div className="connections-section-title">{t('connections.hostingSection')}</div>
        <div className="connections-list">
          {DEPLOY_PROVIDERS.map(p => {
            const state = deployState[p.id] || { connected: false, loading: true };
            return (
              <ProviderCard
                key={p.id}
                name={PROVIDER_NAMES[p.id]}
                description={t(p.descriptionKey)}
                connected={state.connected}
                accountLabel={state.accountName || null}
                loading={state.loading}
                busy={busyId === p.id}
                onConnect={() => handleConnect(p.id, p.apiPrefix)}
                onDisconnect={() => handleDisconnect(p.id, p.apiPrefix)}
              />
            );
          })}

          <div className="connection-card">
            <div className="connection-card-main">
              <div className="connection-card-name">Google Business</div>
              <p className="connection-card-description">{t('connections.googleDescription')}</p>
            </div>
            <div className="connection-card-side">
              <span className={`connection-status-badge ${google.connected ? 'connected' : 'disconnected'}`}>
                <span className="connection-status-dot" />
                {google.loading ? t('common.loading') : google.connected ? t('connections.connected') : t('connections.notConnected')}
              </span>
              {!google.loading && (
                <button type="button" className="btn connection-disconnect-btn" onClick={onNavigateToPublishing}>{t('connections.manageInPublishing')}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConnectionsPanel;
